import { StyledSelect } from "../../primitives/styled-select";
import * as React from "react";
import { Eye, EyeOff, PenLine } from "lucide-react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { cn } from "../../lib/cn";

/**
 * A single signature-layer chip (e.g. "La mia firma" / "Firma aziendale").
 * Shown only when more than one layer exists AND the signature is included.
 */
export interface EmailSignatureLayer {
  readonly key: string;
  readonly label: string;
  readonly active: boolean;
  readonly onSelect: () => void;
}

/** A pickable pool signature (multi-signature "pool" model). */
export interface EmailSignatureOption {
  readonly id: string;
  readonly name: string;
}

export interface EmailSignatureFooterProps {
  /** Already-sanitised signature HTML (caller sanitises before passing). */
  readonly html: string;
  /** Whether the signature is included / appended on send. */
  readonly included: boolean;
  /** Toggle handler for the Eye / EyeOff switch. */
  readonly onToggle: () => void;
  /**
   * Optional signature-layer chips. Rendered only when there is more than one
   * layer AND the signature is currently ``included``. Legacy fallback used
   * when no pool ``signatures`` are supplied.
   */
  readonly layers?: ReadonlyArray<EmailSignatureLayer>;
  /**
   * Optional POOL of selectable signatures (multi-signature model). When more
   * than one is supplied AND the signature is ``included``, a compact dropdown
   * lets the operator pick ANY pool signature for this email. Takes precedence
   * over ``layers``. ``data`` comes from props — this stays presentational.
   */
  readonly signatures?: ReadonlyArray<EmailSignatureOption>;
  /** Id of the currently-active pool signature (``null`` = global default). */
  readonly activeSignatureId?: string | null;
  /** Fired when the operator picks a pool signature from the dropdown. */
  readonly onPick?: (id: string) => void;
  /** Optional small caption shown in the header area. */
  readonly note?: React.ReactNode;
  readonly className?: string;
}

/**
 * EmailSignatureFooter — the shared "Firma" footer rendered inside the email
 * compose sheet (``EmailComposeSheet``'s ``signature`` slot), used by BOTH the
 * inbox reply composer AND the send-email HITL preview so the label +
 * Inclusa/Nascosta Eye toggle + muted preview are pixel-identical.
 *
 * Controlled: the caller owns ``included`` and the append/commit logic; this
 * component only renders the chrome and calls ``onToggle`` / ``layer.onSelect``.
 */
export const EmailSignatureFooter: React.FC<EmailSignatureFooterProps> = ({
  html,
  included,
  onToggle,
  layers,
  signatures,
  activeSignatureId,
  onPick,
  note,
  className,
}) => {
  const { i18n } = useLingui();
  // The pool picker wins over the legacy layer chips when a pool is supplied.
  const showPicker =
    included && signatures != null && signatures.length > 1 && onPick != null;
  const showLayers = !showPicker && included && layers != null && layers.length > 1;

  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 font-medium text-[10px] text-ink-4 uppercase tracking-[0.06em]">
            <PenLine className="h-3 w-3" strokeWidth={2} aria-hidden />
            <Trans>Firma</Trans>
          </span>
          {showPicker ? (
            <StyledSelect
              value={activeSignatureId ?? ""}
              onChange={(e) => onPick?.(e.target.value)}
              aria-label={i18n._(msg`Scegli la firma`)}
              className="max-w-[180px] rounded-md border border-hairline bg-white px-1.5 py-0.5 text-[11px] text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40"
            >
              {signatures.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </StyledSelect>
          ) : null}
          {showLayers ? (
            <div className="flex flex-wrap gap-0.5">
              {layers.map((layer) => (
                <button
                  key={layer.key}
                  type="button"
                  onClick={layer.onSelect}
                  aria-pressed={layer.active}
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[10.5px] transition-colors",
                    layer.active
                      ? "font-medium text-ando-orange"
                      : "text-ink-3 hover:text-ink-1",
                  )}
                >
                  {layer.label}
                </button>
              ))}
            </div>
          ) : null}
          {note ? (
            <span className="text-[10px] text-ink-4">{note}</span>
          ) : null}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={included}
          onClick={onToggle}
          title={
            included
              ? i18n._(msg`La firma verrà aggiunta all'invio`)
              : i18n._(msg`La firma non verrà aggiunta`)
          }
          className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] transition-colors hover:bg-black/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
        >
          {included ? (
            <>
              <Eye className="h-3.5 w-3.5 text-ando-orange" strokeWidth={2} aria-hidden />
              <span className="text-ink-2">
                <Trans>Inclusa</Trans>
              </span>
            </>
          ) : (
            <>
              <EyeOff className="h-3.5 w-3.5 text-ink-3" strokeWidth={2} aria-hidden />
              <span className="text-ink-3">
                <Trans>Nascosta</Trans>
              </span>
            </>
          )}
        </button>
      </div>
      {included ? (
        <div
          className="text-[12px] text-ink-3 leading-relaxed [&_a]:text-ando-orange [&_img]:my-0.5 [&_img]:inline-block [&_img]:max-h-[52px]"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : null}
    </div>
  );
};
